import {
    Badge,
    Group,
    Loader,
    Select,
    Stack,
    Table,
    Tabs,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    AdminCityPurchaseListItemRO,
    AdminCityPurchaseProviderEnum,
} from '@routellers/types/dist/openapi/city-payment/admin-city-purchase.ro';
import { CityPaymentScopeDtoEnum } from '@routellers/types/dist/openapi/city-payment/create-checkout-session.dto';
import { IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { CitySearchSelect } from '../editors/city-search-select';
import { PurchaseDetailDrawer } from '../editors/purchase-detail-drawer';
import { slicePage } from '../utils/list';
import { formatDateTime, formatMoneyCents } from '../utils/portal';

const ALL = 'all';

const SCOPES: CityPaymentScopeDtoEnum[] = [
    CityPaymentScopeDtoEnum.ROUTE,
    CityPaymentScopeDtoEnum.QUEST,
    CityPaymentScopeDtoEnum.CITY,
    CityPaymentScopeDtoEnum.PASS,
];

export const PurchasesPage = () => {
    const { t } = useTranslation();
    const [scope, setScope] = useState<CityPaymentScopeDtoEnum>(CityPaymentScopeDtoEnum.ROUTE);
    const [rows, setRows] = useState<AdminCityPurchaseListItemRO[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [providerFilter, setProviderFilter] = useState<string>(ALL);
    const [refundedFilter, setRefundedFilter] = useState<string>(ALL);
    const [cityId, setCityId] = useState<number | null>(null);
    const [q, setQ] = useState('');
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const providerOptions = useMemo(
        () => [
            { value: ALL, label: t('pages.purchases.filters.allProviders') },
            {
                value: AdminCityPurchaseProviderEnum.STRIPE,
                label: t('enums.purchaseProvider.stripe'),
            },
            { value: AdminCityPurchaseProviderEnum.IAP, label: t('enums.purchaseProvider.iap') },
            {
                value: AdminCityPurchaseProviderEnum.CLOUDPAYMENTS,
                label: t('enums.purchaseProvider.cloudpayments'),
            },
        ],
        [t],
    );

    const refundedOptions = useMemo(
        () => [
            { value: ALL, label: t('pages.purchases.filters.allStatuses') },
            { value: 'false', label: t('pages.purchases.status.paid') },
            { value: 'true', label: t('pages.purchases.status.refunded') },
        ],
        [t],
    );

    useEffect(() => {
        const handle = window.setTimeout(() => setSearch(q.trim()), 300);
        return () => window.clearTimeout(handle);
    }, [q]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.cityPurchasesAdminApi.query({
                scope,
                provider:
                    providerFilter === ALL
                        ? undefined
                        : (providerFilter as AdminCityPurchaseProviderEnum),
                refunded:
                    refundedFilter === ALL ? undefined : refundedFilter === 'true',
                cityId: cityId ?? undefined,
                q: search || undefined,
            });
            setRows(data || []);
            setPage(1);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [scope, providerFilter, refundedFilter, cityId, search]);

    useEffect(() => {
        load();
    }, [load]);

    const pageRows = slicePage(rows, page);

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.purchases.title')}
            />

            <Tabs
                value={scope}
                onChange={(value) => {
                    if (!value) return;
                    const next = value as CityPaymentScopeDtoEnum;
                    setScope(next);
                    if (next === CityPaymentScopeDtoEnum.PASS) setCityId(null);
                }}
            >
                <Tabs.List>
                    {SCOPES.map((s) => (
                        <Tabs.Tab key={s} value={s}>
                            {t(`pages.purchases.tabs.${s}`)}
                        </Tabs.Tab>
                    ))}
                </Tabs.List>
            </Tabs>

            <Group gap='sm' align='flex-end' wrap='wrap'>
                <TextInput
                    label={t('pages.purchases.filters.search')}
                    placeholder={t('pages.purchases.filters.searchPlaceholder')}
                    leftSection={<IconSearch size={16} />}
                    value={q}
                    onChange={(e) => setQ(e.currentTarget.value)}
                    style={{ minWidth: 220, flex: 1 }}
                />
                {scope !== CityPaymentScopeDtoEnum.PASS && (
                    <div style={{ minWidth: 200 }}>
                        <CitySearchSelect
                            label={t('pages.purchases.filters.city')}
                            value={cityId}
                            onChange={setCityId}
                            clearable
                        />
                    </div>
                )}
                <Select
                    label={t('pages.purchases.filters.provider')}
                    data={providerOptions}
                    value={providerFilter}
                    onChange={(v) => setProviderFilter(v || ALL)}
                    allowDeselect={false}
                    style={{ minWidth: 160 }}
                />
                <Select
                    label={t('pages.purchases.filters.status')}
                    data={refundedOptions}
                    value={refundedFilter}
                    onChange={(v) => setRefundedFilter(v || ALL)}
                    allowDeselect={false}
                    style={{ minWidth: 160 }}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={960}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.purchases.columns.date')}</Table.Th>
                                    <Table.Th>{t('pages.purchases.columns.buyer')}</Table.Th>
                                    <Table.Th>{t('pages.purchases.columns.product')}</Table.Th>
                                    <Table.Th ta='right'>
                                        {t('pages.purchases.columns.amount')}
                                    </Table.Th>
                                    <Table.Th>{t('pages.purchases.columns.provider')}</Table.Th>
                                    <Table.Th>{t('pages.purchases.columns.partner')}</Table.Th>
                                    <Table.Th>{t('pages.purchases.columns.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {pageRows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedId(row.id)}
                                    >
                                        <Table.Td>{formatDateTime(row.createdAt)}</Table.Td>
                                        <Table.Td>
                                            <EntityThumb
                                                name={row.player.fullName || row.player.email}
                                                subtitle={
                                                    row.player.fullName
                                                        ? row.player.email
                                                        : undefined
                                                }
                                                radius='xl'
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <EntityThumb
                                                name={row.title}
                                                subtitle={row.cityTitle || undefined}
                                                radius='md'
                                            />
                                        </Table.Td>
                                        <Table.Td ta='right'>
                                            {formatMoneyCents(row.amountPaidCents, row.currency)}
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={
                                                    row.provider === 'stripe' ? 'violet' : 'blue'
                                                }
                                                variant='light'
                                            >
                                                {t(`enums.purchaseProvider.${row.provider}`)}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            {row.partner
                                                ? row.partner.companyName || row.partner.name
                                                : '—'}
                                        </Table.Td>
                                        <Table.Td>
                                            {row.refundedAt ? (
                                                <Badge color='red' variant='light'>
                                                    {t('pages.purchases.status.refunded')}
                                                </Badge>
                                            ) : (
                                                <Badge color='green' variant='light'>
                                                    {t('pages.purchases.status.paid')}
                                                </Badge>
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!pageRows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={7}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.purchases.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={rows.length} onChange={setPage} />
                </>
            )}

            <PurchaseDetailDrawer
                opened={selectedId != null}
                purchaseId={selectedId}
                onClose={() => setSelectedId(null)}
            />
        </Stack>
    );
};
